import Phaser from 'phaser';
import { AboutScene } from './scenes/AboutScene.js';
import { AuthScene } from './scenes/AuthScene.js';
import { ForgotPasswordScene } from './scenes/ForgotPasswordScene.js';
import { FriendsScene } from './scenes/FriendsScene.js';
import { GameScene } from './scenes/GameScene.js';
import { HomeScene } from './scenes/HomeScene.js';
import { HowToScene } from './scenes/HowToScene.js';
import { InboxScene } from './scenes/InboxScene.js';
import { JoinScene } from './scenes/JoinScene.js';
import { LobbyScene } from './scenes/LobbyScene.js';
import { ResetPasswordScene } from './scenes/ResetPasswordScene.js';
import { ResultsScene } from './scenes/ResultsScene.js';
import { SettingsScene } from './scenes/SettingsScene.js';
import { UsernameSetupScene } from './scenes/UsernameSetupScene.js';
import { COLORS } from './style.js';

export const DESIGN_WIDTH = 720;
export const DESIGN_HEIGHT = 1280;

// Safe areas are handled INSIDE the design canvas (topSafe / bottomSafe
// reserved as no-content zones) rather than via CSS `env(safe-area-inset-*)`
// padding on the body. Reasoning: Phaser FIT scales the 720×1280 canvas
// onto whatever viewport is available; adding env() padding to the body
// would shrink that available viewport and shrink the canvas with it.
// Instead we let `viewport-fit=cover` give Phaser the full edge-to-edge
// page, and the in-canvas reservation keeps game UI clear of the iOS
// status bar / notch. The pattern works for both browser-tab and
// installed-PWA modes.
export const LAYOUT = {
  topSafe: 50,
  bottomSafe: 40,
  // HUD is sized to fit the full-width logo (1078×289 → 720 wide → ~193px
  // tall). With the unified page header (logo now at y=0 instead of
  // y=topSafe), HUD background fills from topSafe (50) to 50+143=193,
  // landing exactly at the logo's bottom edge — no visible empty band
  // below the wordmark. Shrunk from 200 → 143 to reclaim that 57px
  // for the map zone, which made the gameplay area noticeably bigger
  // without touching any other layout band.
  //
  // The 64px nav bar at the bottom is reserved for app-level navigation
  // (home / games / chat / leaderboards / profile). Dice tray sits
  // between the map and the action panel; action panel shrunk previously
  // from 326 → 260 to tighten footer empty space.
  hudHeight: 143,
  diceTrayHeight: 84,
  actionHeight: 260,
  navHeight: 64,
} as const;

// Vertical translation applied to every map-tile y-coordinate at
// render time. Map data files still declare positions in their
// "natural" coordinate system; this offset lets the rendering layer
// re-center the territories within the current map zone without
// touching the data.
//
// History: the map zone grew by 57px when hudHeight dropped from 200
// to 143, pulling its center from y=541 up to y=512.5. The legacy map
// data was authored against the 250-832 zone, so territories now sit
// too low. -32 splits the difference across the three shipped maps:
//   - Ring (tile-bbox center y=550)  → 518  (-5.5 vs new center)
//   - Slash (y=540)                  → 508  (-4.5)
//   - Terra (y=541)                  → 509  (-3.5)
// Everyone lands within ~6px of the new map-zone center, which reads
// as "roughly centered" without any map looking pulled up or down.
export const MAP_Y_OFFSET = -32;

export interface BootOptions {
  /** If set, the game boots directly into JoinScene for this game id
   *  (skips Home). Used when the user lands on a /game/:id URL. */
  readonly joinGameId?: string;
  /** If set, the game boots directly into ResetPasswordScene. Used
   *  when the user lands on /reset-password from a magic link in the
   *  password-reset email. */
  readonly resetPassword?: boolean;
}

export function startGame(opts: BootOptions = {}): Phaser.Game {
  // HomeScene is always first → Phaser auto-starts it cleanly. For a
  // /game/:id deep link, we wait for Phaser's READY event and only
  // then swap to JoinScene with the gameId payload. The naive "put
  // Join first" approach makes Phaser auto-init it with no data,
  // which makes init() throw and leaves a black screen.
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'app',
    // Match the body background (#060d17 in index.html) exactly. With
    // Phaser FIT scale, any device whose aspect ratio isn't 9:16 gets
    // letterbox bars showing the body color — a mismatch here paints a
    // visible seam between the canvas and the bars on every iPhone
    // taller than 9:16 (which is most of them).
    backgroundColor: COLORS.bgDeepHex,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: DESIGN_WIDTH,
      height: DESIGN_HEIGHT,
    },
    scene: [
      HomeScene,
      LobbyScene,
      GameScene,
      JoinScene,
      AboutScene,
      HowToScene,
      InboxScene,
      SettingsScene,
      AuthScene,
      UsernameSetupScene,
      ForgotPasswordScene,
      ResetPasswordScene,
      ResultsScene,
      FriendsScene,
    ],
  });

  const target = opts.joinGameId;
  if (target) {
    game.events.once(Phaser.Core.Events.READY, () => {
      game.scene.stop('Home');
      game.scene.start('Join', { gameId: target });
    });
  } else if (opts.resetPassword) {
    // /reset-password deep link from the email magic link. Same
    // dance as joinGameId — wait for READY, swap out the default
    // HomeScene start with the reset flow. detectSessionInUrl on
    // the Supabase client picks up the access_token from the URL
    // fragment so ResetPasswordScene mounts already-signed-in.
    game.events.once(Phaser.Core.Events.READY, () => {
      game.scene.stop('Home');
      game.scene.start('ResetPassword');
    });
  }

  return game;
}
